import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Button,
  Collapse,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import faceImg from '../../../assets/aesthetic-procedure/face.png';
import bodyImg from '../../../assets/aesthetic-procedure/body.png';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/errorMessage';
import SuccessMessage from '../../../components/successMessage';
import { useProcedureApplications } from './hooks/useProcedureApplications';

const APPLICATION_COLOR = '#0d9488';

export default function ProcedureApplicationMapWeb({
  applicationsBaseUrl,
  initialMapType,
  isFinished,
}) {
  const navigate = useNavigate();

  const {
    fetchApplications: fetchApplicationsRequest,
    saveApplication,
    deleteApplication,
  } = useProcedureApplications(applicationsBaseUrl);

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [mapType, setMapType] = useState(initialMapType || 'FACIAL_MAP');
  const [markers, setMarkers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [collapsed, setCollapsed] = useState(new Set());

  const nextId = useRef(1);
  const imgRef = useRef(null);

  const toggleCollapse = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const fetchApplications = useCallback(() => {
    if (!applicationsBaseUrl) return;
    setLoading(true);
    fetchApplicationsRequest()
      .then((returnFromApi) => {
        const applications = returnFromApi.data.map((a) => ({
          id: a.id,
          backendId: a.id,
          x: a.mapPositionX,
          y: a.mapPositionY,
          product: a.productDescription ?? '',
          quantity: a.quantity ?? '',
          batch: a.batch ?? '',
          expirationDate: a.expirationDate ? dayjs(a.expirationDate) : null,
        }));
        setMarkers(applications);
        setCollapsed(new Set(applications.map((a) => a.id)));
        const maxId = applications.reduce((max, a) => Math.max(max, a.id), 0);
        nextId.current = maxId + 1;
        const existingMapType = returnFromApi.data.find((a) => a.mapType)?.mapType;
        if (existingMapType) setMapType(existingMapType);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setLoading(false));
  }, [applicationsBaseUrl, navigate, fetchApplicationsRequest]);

  useEffect(() => {
    setSelected(null);
    fetchApplications();
  }, [fetchApplications]);

  const handleClickImage = (e) => {
    if (isFinished) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const newMarker = {
      id: nextId.current++,
      backendId: null,
      x: Math.min(Math.max(x, 0), 1),
      y: Math.min(Math.max(y, 0), 1),
      product: '',
      quantity: '',
      batch: '',
      expirationDate: null,
    };
    setMarkers((prev) => [...prev, newMarker]);
    setSelected(newMarker.id);
  };

  const updateMarker = (id, field, value) =>
    setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));

  const handleSaveApplication = (e, marker) => {
    e.stopPropagation();
    const payload = {
      productDescription: marker.product,
      quantity: marker.quantity ? parseInt(marker.quantity) : null,
      batch: marker.batch,
      expirationDate: marker.expirationDate ? marker.expirationDate.format('YYYY-MM-DD') : null,
      mapType,
      mapPositionX: marker.x,
      mapPositionY: marker.y,
    };
    setLoading(true);

    if (marker.backendId) {
      saveApplication(marker.backendId, payload)
        .then(() => {
          setSuccessMessage('Aplicação atualizada com sucesso.');
          setShowSuccessMessage(true);
        })
        .catch((returnFromApi) => {
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          console.error('ops! ocorreu um erro' + returnFromApi);
          if (returnFromApi.status === 401) {
            navigate('/expired-session');
          }
        })
        .finally(() => setLoading(false));
    } else {
      saveApplication(null, payload)
        .then((returnFromApi) => {
          setSuccessMessage(returnFromApi.data);
          setShowSuccessMessage(true);
          fetchApplications();
        })
        .catch((returnFromApi) => {
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          console.error('ops! ocorreu um erro' + returnFromApi);
          if (returnFromApi.status === 401) {
            navigate('/expired-session');
          }
        })
        .finally(() => setLoading(false));
    }
  };

  const handleDeleteApplication = (e, marker) => {
    e.stopPropagation();
    if (!marker.backendId) {
      setMarkers((prev) => prev.filter((m) => m.id !== marker.id));
      if (selected === marker.id) setSelected(null);
      return;
    }
    setLoading(true);
    deleteApplication(marker.backendId)
      .then((returnFromApi) => {
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
        setMarkers((prev) => prev.filter((m) => m.id !== marker.id));
        if (selected === marker.id) setSelected(null);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Left column: image + observation */}
        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={mapType === 'FACIAL_MAP'}
                  onChange={() => setMapType('FACIAL_MAP')}
                  disabled={isFinished || markers.length > 0}
                />
              }
              label="Facial"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={mapType === 'BODY_MAP'}
                  onChange={() => setMapType('BODY_MAP')}
                  disabled={isFinished || markers.length > 0}
                />
              }
              label="Corporal"
            />
          </FormGroup>
          {markers.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              O tipo do mapa não pode ser alterado após registrar aplicações.
            </Typography>
          )}
          <Box
            ref={imgRef}
            onClick={handleClickImage}
            sx={{ position: 'relative', cursor: isFinished ? 'default' : 'crosshair' }}
          >
            <Box
              component="img"
              src={mapType === 'FACIAL_MAP' ? faceImg : bodyImg}
              alt={mapType === 'FACIAL_MAP' ? 'Mapa facial' : 'Mapa corporal'}
              sx={{ width: 420, display: 'block', objectFit: 'contain' }}
            />
            {markers.map((marker, i) => {
              const color = APPLICATION_COLOR;
              const active = selected === marker.id;
              return (
                <Box
                  key={marker.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(marker.id);
                  }}
                  sx={{
                    position: 'absolute',
                    left: `${marker.x * 100}%`,
                    top: `${marker.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: color,
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #fff',
                    cursor: 'pointer',
                    boxShadow: active ? `0 0 0 4px ${color}60` : '0 1px 3px rgba(0,0,0,0.3)',
                    zIndex: 1,
                  }}
                >
                  {i + 1}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Right column: applications card */}
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardHeader
            title={
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Aplicações ({markers.length})
              </Typography>
            }
            sx={{ py: 1, px: 1.5 }}
          />
          <CardContent sx={{ maxHeight: 480, overflowY: 'auto', p: 1, '&:last-child': { pb: 1 } }}>
            {markers.length === 0 ? (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Clique na imagem para adicionar uma aplicação.
                </Typography>
              </Box>
            ) : (
              <>
                {markers.map((marker, i) => {
                  const color = APPLICATION_COLOR;
                  const active = selected === marker.id;
                  return (
                    <Box
                      key={marker.id}
                      onClick={() => setSelected(marker.id)}
                      sx={{
                        border: '1px solid',
                        borderColor: active ? color : 'divider',
                        bgcolor: active ? `${color}0d` : 'transparent',
                        borderRadius: 1,
                        p: 1,
                        mb: 0.75,
                        cursor: 'pointer',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: collapsed.has(marker.id) ? 0 : 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              bgcolor: color,
                              color: '#fff',
                              fontSize: '10px',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {i + 1}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              Aplicação {i + 1}
                            </Typography>
                            {collapsed.has(marker.id) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', fontSize: '0.65rem' }}
                              >
                                {marker.product}
                                {marker.batch ? ` - Lote ${marker.batch}` : ''}
                                {marker.quantity ? ` - ${marker.quantity}` : ''}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCollapse(marker.id);
                            }}
                            sx={{ color: 'text.disabled', p: 0.25 }}
                          >
                            {collapsed.has(marker.id) ? (
                              <ExpandMoreIcon sx={{ fontSize: 15 }} />
                            ) : (
                              <ExpandLessIcon sx={{ fontSize: 15 }} />
                            )}
                          </IconButton>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleSaveApplication(e, marker)}
                            disabled={isFinished}
                            sx={{ py: 0, px: 1, minWidth: 0, fontSize: '0.7rem' }}
                          >
                            Salvar
                          </Button>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteApplication(e, marker)}
                            disabled={isFinished}
                            sx={{
                              color: 'text.disabled',
                              p: 0.25,
                              '&:hover': {
                                color: isFinished ? 'text.disabled' : 'error.main',
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Box>
                      </Box>
                      <Collapse in={!collapsed.has(marker.id)}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Produto"
                          value={marker.product}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateMarker(marker.id, 'product', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.75rem' } }}
                          sx={{ mb: 0.5 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Qtd."
                          value={marker.quantity}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateMarker(marker.id, 'quantity', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.75rem' } }}
                          inputProps={{ min: 0, step: 0.1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            label="Lote"
                            value={marker.batch}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateMarker(marker.id, 'batch', e.target.value)}
                            InputProps={{ sx: { fontSize: '0.75rem' } }}
                            sx={{ flex: 1 }}
                          />
                          <DatePicker
                            label="Validade"
                            value={marker.expirationDate}
                            onChange={(val) => updateMarker(marker.id, 'expirationDate', val)}
                            format="MM/YYYY"
                            views={['month', 'year']}
                            slotProps={{
                              textField: {
                                size: 'small',
                                onClick: (e) => e.stopPropagation(),
                                InputProps: { sx: { fontSize: '0.75rem' } },
                                sx: { flex: 1 },
                              },
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

ProcedureApplicationMapWeb.propTypes = {
  applicationsBaseUrl: PropTypes.string.isRequired,
  initialMapType: PropTypes.string,
  isFinished: PropTypes.bool,
};

ProcedureApplicationMapWeb.defaultProps = {
  initialMapType: 'FACIAL_MAP',
  isFinished: false,
};
